package com.anjuros.website.redesign.sections.designs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdLink
import com.anjuros.website.redesign.components.RdPicture
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.components.RdSectionHead
import com.anjuros.website.redesign.components.RdTag
import com.anjuros.website.redesign.styles.DesignsAlsoStyle
import com.anjuros.website.redesign.styles.DesignsBadgeStyle
import com.anjuros.website.redesign.styles.DesignsCardBodyStyle
import com.anjuros.website.redesign.styles.DesignsCardLinkStyle
import com.anjuros.website.redesign.styles.DesignsCardStyle
import com.anjuros.website.redesign.styles.DesignsCatalogueStyle
import com.anjuros.website.redesign.styles.DesignsCountStyle
import com.anjuros.website.redesign.styles.DesignsFilterActiveStyle
import com.anjuros.website.redesign.styles.DesignsFilterStyle
import com.anjuros.website.redesign.styles.DesignsFiltersStyle
import com.anjuros.website.redesign.styles.RdH3Style
import com.anjuros.website.redesign.styles.RdPillStyle
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** All designs with a filter by type of business. Every card stays in the page, so filtering never hides content from search engines. */
@Composable
fun DesignsCatalogueSection() {
    var filter by remember { mutableStateOf<DesignCategory?>(null) }
    val shown = AllDesigns.count { filter == null || it.category == filter }

    RdSection(id = "catalogue", labelledBy = "cat-h") {
        RdSectionHead(RdText.DESIGNS_CAT_EYEBROW, "cat-h", RdText.DESIGNS_CAT_TITLE, RdText.DESIGNS_CAT_LEDE)
        Filters(selected = filter, onSelect = { filter = it })
        Div(DesignsCatalogueStyle.toModifier().toAttrs()) {
            AllDesigns.forEach { design ->
                DesignCard(design, visible = filter == null || design.category == filter)
            }
        }
        P(DesignsCountStyle.toModifier().attrsModifier { attr("aria-live", "polite") }.toAttrs()) {
            Text(
                if (filter == null) LocalText(RdText.DESIGNS_COUNT_ALL)
                else LocalText(RdText.DESIGNS_COUNT_SOME).replace("%d", shown.toString())
            )
        }
    }
}

@Composable
private fun Filters(selected: DesignCategory?, onSelect: (DesignCategory?) -> Unit) {
    val label = LocalText(RdText.DESIGNS_FILTER_LABEL)
    Div(DesignsFiltersStyle.toModifier().attrsModifier { attr("role", "group"); attr("aria-label", label) }.toAttrs()) {
        FilterButton(RdText.DESIGNS_FILTER_ALL, active = selected == null) { onSelect(null) }
        DesignCategory.entries.forEach { category ->
            FilterButton(category.label, active = selected == category) { onSelect(category) }
        }
    }
}

@Composable
private fun FilterButton(text: RdText, active: Boolean, onClick: () -> Unit) {
    Button(
        DesignsFilterStyle.toModifier()
            .thenIf(active, DesignsFilterActiveStyle.toModifier())
            .attrsModifier {
                attr("type", "button")
                attr("aria-pressed", active.toString())
            }
            .onClick { onClick() }
            .toAttrs()
    ) { Text(LocalText(text)) }
}

@Composable
private fun DesignCard(design: DesignEntry, visible: Boolean) {
    Article(DesignsCardStyle.toModifier().attrsModifier { if (!visible) attr("hidden", "") }.toAttrs()) {
        RdTag("figure") {
            RdPicture(design.image, LocalText(design.alt))
            if (design.featured) {
                Span(RdPillStyle.toModifier().then(DesignsBadgeStyle.toModifier()).toAttrs()) { Text(LocalText(RdText.FEATURED)) }
            }
        }
        Div(DesignsCardBodyStyle.toModifier().toAttrs()) {
            H3(RdH3Style.toModifier().toAttrs()) { Text(LocalText(design.title)) }
            P { Text(LocalText(design.text)) }
            P(DesignsAlsoStyle.toModifier().toAttrs()) {
                Span { Text(LocalText(RdText.DESIGNS_ALSO_SUITS)) }
                rdList(LocalText(design.alsoSuits)).forEach { Span { Text(it) } }
            }
            RdLink(design.path, DesignsCardLinkStyle.toModifier()) {
                Text(LocalText(design.linkText))
                Span({ attr("aria-hidden", "true") }) { Text("→") }
            }
        }
    }
}
